import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  AlignmentType,
  BorderStyle,
  WidthType,
  ShadingType,
  VerticalAlign,
  TableLayoutType,
} from 'docx';
import { saveAs } from 'file-saver';
import { GoalDocument } from '../types';
import { format } from 'date-fns';

// Constants
const FONT = 'Calibri';
const FONT_SIZE = 22; // 11pt
const CURSIVE_FONT = 'Segoe Script';
const GREEN_FILL = 'B3E5A1';
const GRAY_FILL = 'ADADAD';

// Border definitions
const thinBorder = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
const thickBorder = { style: BorderStyle.SINGLE, size: 8, color: '000000' };
const noBorder = { style: BorderStyle.NIL, size: 0, color: '000000' };

const allThinBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
const allThickBorders = { top: thickBorder, bottom: thickBorder, left: thickBorder, right: thickBorder };

// Text helper
const txt = (content: string, opts: { bold?: boolean; size?: number; font?: string; italic?: boolean } = {}): TextRun => {
  return new TextRun({
    text: content,
    font: opts.font || FONT,
    size: opts.size || FONT_SIZE,
    bold: opts.bold || false,
    italics: opts.italic || false,
  });
};

// Cursive signature
const sig = (name: string): TextRun => {
  return new TextRun({
    text: name,
    font: CURSIVE_FONT,
    size: 24,
    italics: true,
  });
};

const fmtDate = (ts: any): string => {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return format(d, 'M/d/yyyy');
  } catch {
    return '';
  }
};

export const generateGoalsDocx = async (goal: GoalDocument): Promise<void> => {
  const goalLabels = ['Normal Goal:', 'Normal Goal:', 'Normal Goal:', 'Stretch Goal:', 'Hard Goal:'];
  const behLabels = ['Respect for Others', 'Integrity', 'Teamwork & Collaboration', 'Bias for Action', 'Careful Communication', 'Growth Mindset', 'Lead by Example'];
  const behKeys = ['respectForOthers', 'integrity', 'teamwork', 'biasForAction', 'carefulCommunication', 'growthMindset', 'leadByExample'] as const;

  // Column widths in DXA (1440 DXA = 1 inch)
  // Total table width ~9360 DXA (6.5 inches)
  // Description: 5400, Each rating col: 540, Gray gap: 300
  // 5400 + (540*3) + 300 + (540*3) = 5400 + 1620 + 300 + 1620 = 8940
  const W_DESC = 5400;
  const W_RATE = 540;
  const W_GAP = 300;
  const TOTAL_WIDTH = W_DESC + (W_RATE * 6) + W_GAP; // 8940

  // Build goal rows
  const goalRows: TableRow[] = [];

  // Single header row
  goalRows.push(new TableRow({
    children: [
      new TableCell({
        width: { size: W_DESC, type: WidthType.DXA },
        borders: allThickBorders,
        shading: { fill: GREEN_FILL, type: ShadingType.CLEAR },
        verticalAlign: VerticalAlign.CENTER,
        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('Goal Description', { bold: true })] })],
      }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('A', { size: 16 })] })] }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('B', { size: 16 })] })] }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('NI', { size: 16 })] })] }),
      new TableCell({
        width: { size: W_GAP, type: WidthType.DXA },
        borders: { top: thickBorder, bottom: thickBorder, left: noBorder, right: noBorder },
        shading: { fill: GRAY_FILL, type: ShadingType.CLEAR },
        children: [new Paragraph({ children: [] })],
      }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('A', { size: 16 })] })] }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('B', { size: 16 })] })] }),
      new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('NI', { size: 16 })] })] }),
    ],
  }));

  // Goal data rows
  goal.goals.forEach((g, i) => {
    goalRows.push(new TableRow({
      children: [
        new TableCell({
          width: { size: W_DESC, type: WidthType.DXA },
          borders: allThinBorders,
          children: [
            new Paragraph({ children: [txt(goalLabels[i], { bold: true }), txt(' '), txt(g.title || '')] }),
            new Paragraph({ children: [txt('Description: ', { bold: true, size: 18 }), txt(g.description || '', { size: 18 })] }),
          ],
        }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.employeeRating === 'A' ? 'X' : '')] })] }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.employeeRating === 'B' ? 'X' : '')] })] }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.employeeRating === 'NI' ? 'X' : '')] })] }),
        new TableCell({ width: { size: W_GAP, type: WidthType.DXA }, borders: { top: thinBorder, bottom: thinBorder, left: noBorder, right: noBorder }, shading: { fill: GRAY_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ children: [] })] }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.managerRating === 'A' ? 'X' : '')] })] }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.managerRating === 'B' ? 'X' : '')] })] }),
        new TableCell({ width: { size: W_RATE, type: WidthType.DXA }, borders: allThinBorders, verticalAlign: VerticalAlign.CENTER, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(g.managerRating === 'NI' ? 'X' : '')] })] }),
      ],
    }));
  });

  // Behavioral rows
  const behRows: TableRow[] = [
    new TableRow({
      children: [
        new TableCell({ columnSpan: 4, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('*Only Manager Fills This Section Out*')] })] }),
      ],
    }),
    new TableRow({
      children: [
        new TableCell({ width: { size: 5000, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ children: [txt('Behavioral Performance', { bold: true })] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('A')] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('B')] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('NI')] })] }),
      ],
    }),
  ];

  behLabels.forEach((label, i) => {
    const rating = goal.behavioralRatings[behKeys[i]];
    behRows.push(new TableRow({
      children: [
        new TableCell({ width: { size: 5000, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ children: [txt(label)] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(rating === 'A' ? 'X' : '')] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(rating === 'B' ? 'X' : '')] })] }),
        new TableCell({ width: { size: 700, type: WidthType.DXA }, borders: allThickBorders, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(rating === 'E' ? 'X' : '')] })] }),
      ],
    }));
  });

  // Create document
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 720, right: 720, bottom: 720, left: 720 },
        },
      },
      children: [
        // Title
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [txt(`Q${goal.quarter} ${goal.year} Goals`, { bold: true, size: 28 })],
        }),

        // Name - Title
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [
            txt(goal.userName, { bold: true, size: 24 }),
            txt(' – ', { bold: true, size: 24 }),
            txt(goal.userTitle || 'Employee', { bold: true, size: 24 }),
          ],
        }),

        new Paragraph({ children: [] }),

        // Incentive table
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ columnSpan: 4, borders: allThinBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt('*Only Manager Fills This Section Out*')] })] }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt('50% of Incentive based on sales goal', { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt(`Sales Goal reached ${goal.salesGoal?.percentage || 'X'} %`, { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt('X % of', { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt(`$${goal.salesGoal?.amount?.toLocaleString() || ''}`, { size: 18 })] })] }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt('50% of Incentive based on performance goal', { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt(`Performance Goal reached ${goal.performanceGoal?.percentage || 'X'} %`, { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt('X % of', { size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt(`$${goal.performanceGoal?.amount?.toLocaleString() || ''}`, { size: 18 })] })] }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [txt('Total:', { bold: true, size: 18 })] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [] })] }),
                new TableCell({ width: { size: 25, type: WidthType.PERCENTAGE }, borders: allThinBorders, children: [new Paragraph({ children: [] })] }),
              ],
            }),
          ],
        }),

        new Paragraph({ children: [] }),

        // Goals header - use same width as goals table
        new Table({
          layout: TableLayoutType.FIXED,
          width: { size: TOTAL_WIDTH, type: WidthType.DXA },
          columnWidths: [W_DESC + (W_RATE * 3), W_GAP + (W_RATE * 3)],
          rows: [
            new TableRow({
              children: [
                new TableCell({ width: { size: W_DESC + (W_RATE * 3), type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [txt(`Q${goal.quarter} ${goal.year} GOALS`, { bold: true, size: 26 })] })] }),
                new TableCell({ width: { size: W_GAP + (W_RATE * 3), type: WidthType.DXA }, borders: allThickBorders, shading: { fill: GREEN_FILL, type: ShadingType.CLEAR }, children: [
                  new Paragraph({ children: [txt('A = At or above expectations', { size: 14 })] }),
                  new Paragraph({ children: [txt('B = Below expectations', { size: 14 })] }),
                  new Paragraph({ children: [txt('NI = Needs Improvement', { size: 14 })] }),
                ]}),
              ],
            }),
          ],
        }),

        // Goals table - FIXED LAYOUT forces Word to use our exact widths
        new Table({
          layout: TableLayoutType.FIXED,
          width: { size: TOTAL_WIDTH, type: WidthType.DXA },
          columnWidths: [W_DESC, W_RATE, W_RATE, W_RATE, W_GAP, W_RATE, W_RATE, W_RATE],
          rows: goalRows,
        }),

        new Paragraph({ children: [] }),

        // Behavioral table
        new Table({
          layout: TableLayoutType.FIXED,
          width: { size: 7100, type: WidthType.DXA },
          columnWidths: [5000, 700, 700, 700],
          rows: behRows,
        }),

        new Paragraph({ children: [] }),
        new Paragraph({ children: [] }),

        // Signatures - Set
        new Paragraph({
          children: [
            ...(goal.signatures.employeeSet?.name
              ? [sig(goal.signatures.employeeSet.name), txt(`  ${fmtDate(goal.signatures.employeeSet.date)}`)]
              : [txt('______________________________')]),
            txt('\t\t\t\t'),
            ...(goal.signatures.managerSet?.name
              ? [sig(goal.signatures.managerSet.name), txt(`  ${fmtDate(goal.signatures.managerSet.date)}`)]
              : [txt('______________________________')]),
          ],
        }),
        new Paragraph({
          children: [
            txt('Employee Signature / Date'),
            txt('\t\t\t      Set\t\t\t'),
            txt('Manager Signature / Date'),
          ],
        }),

        new Paragraph({ children: [] }),

        // Signatures - Reviewed
        new Paragraph({
          children: [
            ...(goal.signatures.employeeReview?.name
              ? [sig(goal.signatures.employeeReview.name), txt(`  ${fmtDate(goal.signatures.employeeReview.date)}`)]
              : [txt('______________________________')]),
            txt('\t\t\t\t'),
            ...(goal.signatures.managerReview?.name
              ? [sig(goal.signatures.managerReview.name), txt(`  ${fmtDate(goal.signatures.managerReview.date)}`)]
              : [txt('______________________________')]),
          ],
        }),
        new Paragraph({
          children: [
            txt('Employee Signature / Date'),
            txt('\t\t\t  Reviewed\t\t\t'),
            txt('Manager Signature / Date'),
          ],
        }),
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Q${goal.quarter}_${goal.year}_Goals_${goal.userName.replace(/\s+/g, '_')}.docx`);
};